import type { TestableComponent } from '@mweb/types';
import { useEffect, useState } from 'react';

import { parseTextIntoSegments, scrollToSelectedTab } from 'components/productdetail/ReviewSummarization/ReviewSummarization.util';
import { Tabs, TabsList } from 'components/productdetail/ReviewSummarization/Tabs';
import { TabsHeader } from 'components/productdetail/ReviewSummarization/TabsHeader/TabsHeader';
import { TabsContent } from 'components/productdetail/ReviewSummarization/TabsContent/TabsContent';
import useMartyContext from 'hooks/useMartyContext';
import type { ReviewSummarizationResponseAspects } from 'types/reviewSummarization';
import { AI_GENERATED } from 'constants/reviews';
import { cn } from 'helpers/classnames';

export interface TabsProps extends TestableComponent {
  aspects: ReviewSummarizationResponseAspects[];
  onTabOpen?: (tab: string) => void;
  selectedTab?: string;
  isSuppressContent?: boolean;
  isWrapHeaderEnabled?: boolean;
  /** Used for scrolling selected tab into view */
  id?: string;
}

export const TabsContainer = ({ id, aspects, testId, selectedTab, isSuppressContent, onTabOpen, isWrapHeaderEnabled = true }: TabsProps) => {
  const { testId: getTestId } = useMartyContext();
  const [openTab, setOpenTab] = useState(selectedTab);

  useEffect(() => {
    setOpenTab(selectedTab);
    scrollToSelectedTab(id);
  }, [selectedTab]);

  const handleOpenTab = (tab: string) => {
    setOpenTab(tab);
  };

  const handleValueChange = (value: string) => {
    setOpenTab(value);
    onTabOpen?.(value);
  };

  return (
    <Tabs id={id} value={openTab} onValueChange={handleValueChange} className="flex flex-col items-start gap-y-3" data-test-id={getTestId(testId)}>
      <TabsList isWrap={isWrapHeaderEnabled}>
        {aspects.map(({ name, sentiment }, index) => (
          <div key={name} className="flex gap-x-2">
            <TabsHeader testId={`${getTestId(testId)}-header-${name}`} sentiment={sentiment} handleOpenTab={handleOpenTab}>
              {name}
            </TabsHeader>
            {index < aspects.length - 1 && <hr className="my-auto h-[20px] border-l border-subtle" />}
          </div>
        ))}
      </TabsList>
      {!isSuppressContent &&
        aspects.map(({ name, summary, totalcount, positivecount, negativecount, snippets }) => (
          <TabsContent
            testId={`${getTestId(testId)}-content-${name}`}
            key={name}
            headerName={name}
            totalcount={totalcount}
            positivecount={positivecount}
            negativecount={negativecount}
            handleOpenTab={handleOpenTab}
          >
            <>
              <div className="flex flex-col gap-y-2">
                <p className={cn('text-base', 'zappos:body-m')}>{summary}</p>
                <p className={cn('text-base text-tertiary sm:hidden', 'zappos:body-m')}>{AI_GENERATED}</p>
              </div>
              <div className="flex flex-col gap-y-3">
                {snippets.map(snippet => {
                  const { beginText, keyphrase, endText } = parseTextIntoSegments({ text: snippet.text, keyphrase: snippet.keyphrase }) ?? {};
                  const finalBeginText = `"${snippet.startindex !== 0 ? `...${beginText}` : beginText}`;
                  const finalEndText = `${endText}${snippet.endindex !== snippet.text.length ? '...' : ''}"`;
                  return (
                    <div className="flex flex-col gap-y-3" key={snippet.reviewId}>
                      <hr className="h-[1px] border-t border-subtle" />
                      <p className={cn('text-base', 'zappos:body-m')}>
                        {finalBeginText}
                        <strong>{keyphrase}</strong>
                        {finalEndText}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          </TabsContent>
        ))}
    </Tabs>
  );
};
